<script setup lang="ts">
const props = defineProps({
  href: { type: String, required: false, default: '' },
  primary: { type: Boolean, default: true },
  lightPrimary: { type: Boolean, default: false },
  black: { type: Boolean, default: false },
  grey: { type: Boolean, default: false },
  red: { type: Boolean, default: false },
  outline: { type: Boolean, default: false },
  arrow: { type: Boolean, default: false },
  small: { type: Boolean, default: false },
  extraSmall: { type: Boolean, default: false },
  square: { type: Boolean, default: false },
  large: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  download: { type: String, default: '' },
  icon: { type: Boolean, default: false },
  mail: { type: String, default: '' }
})

defineEmits(['handleClick'])

const getBtnClass = () => {
  let btnClass = ' btn-hover '

  // colour and border
  if (props.disabled && !props.outline) btnClass += 'btn-disabled '
  else if (props.disabled && props.outline) btnClass += 'btn-disabled-outline '
  else if (props.black && !props.outline) btnClass += 'btn-black '
  else if (props.black && props.outline) btnClass += 'btn-black-outline '
  else if (props.red && !props.outline) btnClass += ' btn-red '
  else if (props.red && props.outline) btnClass += ' btn-red-outline '
  else if (props.primary && props.outline) btnClass += 'btn-primary-outline '
  else if (props.grey) btnClass += ' btn-grey '
  else if (props.lightPrimary) btnClass += ' btn-light-primary '
  else btnClass += 'btn-primary '

  // size and arrow
  if (props.arrow) btnClass += 'btn-arrow'
  else if (props.icon) {
    if (props.small) btnClass += 'btn-icon-s'
    else if (props.large) btnClass += 'btn-icon-l'
    else btnClass += 'btn-icon-m'
  } else if (props.small) btnClass += 'btn-s'
  else if (props.large) btnClass += 'btn-l'
  else if (props.extraSmall) btnClass += 'btn-xs'
  else if (props.square) btnClass += 'btn-sq'
  else btnClass += 'btn-m'

  return btnClass
}
</script>

<template>
  <NuxtLink
    v-if="download === '' && mail === ''"
    :to="href"
    :class="getBtnClass()"
    @click="$emit('handleClick')"
  >
    <slot /> <CommonIconArrow v-if="arrow" />
  </NuxtLink>
  <a v-else-if="download" :class="getBtnClass()" :download="download" :href="href">
    <slot />
  </a>
  <a v-else-if="mail" :class="getBtnClass()" :href="`mailto:${mail}`">
    <slot />
  </a>
  <a v-else :class="getBtnClass()" :href="href">
    <slot />
  </a>
</template>

<style scoped lang="scss">
// ----------------
// BUTTON
// ----------------

@mixin buttonBase($bg, $border, $color) {
  font-weight: 500;
  font-size: 0.9rem;
  transition:
    color 0.4s ease-out,
    background 0.4s ease-out,
    border 0.4s ease-out;
  background-color: $bg;
  border-color: $border;
  border-width: 1px;
  border-style: solid;
  color: $color;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  user-select: none;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;

  span,
  :deep(span) {
    transition: color 0.4s ease-out;
  }

  :deep(svg),
  svg {
    $iconSize: 13px;
    flex-shrink: 0;
    height: $iconSize;
    width: $iconSize;
    fill: $color;
    transition:
      fill 0.4s ease-out,
      transform 0.4s ease-out;
  }
}

@mixin button($mainColor) {
  @include buttonBase($mainColor, $mainColor, white);
}

@mixin buttonOutline($mainColor) {
  @include buttonBase(transparent, $mainColor, $mainColor);
}

@mixin buttonHover($bg, $border, $color) {
  background-color: $bg;
  border-color: $border;
  color: $color;

  :deep(svg),
  svg {
    fill: $color;
  }
}

@mixin iconBtn() {
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  :deep(svg) {
    $size: 15px;
    height: $size;
    width: $size;
  }
}

.btn {
  $grey: rgb(111, 130, 142);

  &-disabled {
    @include button($grey);
    pointer-events: none;
  }

  &-disabled-outline {
    @include buttonOutline($grey);
    pointer-events: none;
  }

  &-primary {
    @include button(var(--purple));
  }

  &-light-primary {
    @include buttonBase(var(--lightPurple), var(--lightPurple), var(--purple));
  }

  &-primary-outline {
    @include buttonOutline(var(--purple));
  }

  &-red-outline {
    @include buttonOutline(var(--forumRed));
  }

  &-black {
    @include button(var(--black));
  }

  &-black-outline {
    @include buttonOutline(var(--black));
  }

  &-grey {
    @include buttonBase(transparent, var(--borderM2), var(--black));
  }

  &-red {
    @include button(var(--forumRed));
  }

  &-xs {
    border-radius: 7px;
    height: 30px;
    min-width: 30px;
    padding: 0 15px;
    font-size: 0.87rem;

    :deep(svg),
    svg {
      height: 10px;
      width: 10px;
    }
  }

  &-sq {
    border-radius: 7px;
    height: 30px;
    width: 30px;

    :deep(svg),
    svg {
      height: 12px;
      width: 12px;
    }
  }

  &-s {
    border-radius: 7px;
    height: 35px;
    padding: 0 20px;

    :deep(svg),
    svg {
      height: 12px;
      width: 12px;
    }
  }

  &-m {
    border-radius: 7px;
    height: 40px;
    padding: 0 25px;
  }

  &-l {
    border-radius: 7px;
    padding: 0 30px;
    text-align: center;
    min-width: 300px;
    height: 45px;
  }

  &-arrow {
    border-radius: 7px;
    height: 40px;
    padding: 0 23px 0 25px;

    svg {
      $size: 12px;
      height: $size;
      width: $size;
    }
  }

  &-icon {
    &-s {
      @include iconBtn();
      padding: 8px 20px;
    }

    &-m {
      @include iconBtn();
      padding: 15px 30px;
    }

    &-l {
      @include iconBtn();
      padding: 18px 30px;
    }
  }
}

@media (max-width: 600px) {
  .btn-s,
  .btn-xs,
  .btn-m,
  .btn-l,
  .btn-arrow {
    height: 45px;
    width: 100%;
    white-space: wrap;
    border-radius: 10px;
  }

  .btn-arrow {
    display: flex;
    align-items: center;

    svg {
      flex-shrink: 0;
    }
  }

  .btn-l {
    min-width: 0;
    padding-right: 35px;
    padding-left: 35px;
  }
}

@media (hover: hover) {
  .btn {
    &-primary:hover {
      @include buttonHover(transparent, var(--purple), var(--purple));
    }

    &-light-primary:hover {
      @include buttonHover(transparent, var(--purple), var(--purple));
    }

    &-primary-outline:hover {
      @include buttonHover(var(--lightPurple), var(--purple), var(--purple));
    }

    &-black:hover {
      @include buttonHover(transparent, var(--black), var(--black));
    }

    &-black-outline:hover {
      @include buttonHover(var(--black), var(--black), white);
    }

    &-red-outline:hover {
      @include buttonHover(var(--forumRed), var(--forumRed), white);
    }

    &-arrow:hover {
      svg {
        transform: translateX(4px);
      }
    }

    &-grey:hover {
      border-color: var(--purple);
      color: var(--purple);

      span,
      :deep(span) {
        color: var(--purple);
      }

      svg,
      :deep(svg) {
        fill: var(--purple);
      }
    }

    &-red:hover {
      @include buttonHover(transparent, var(--forumRed), var(--forumRed));
    }
  }
}
</style>
